import React, { useState, useEffect } from 'react';
import { PlusIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import DataTable from '../components/common/DataTable';
import { leaveService } from '../services/hrService';
import { formatDate } from '../utils/format';
import { useAuth } from '../context/AuthContext';

export default function LeaveRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: ''
  });

  useEffect(() => {
    fetchRequests();
    fetchLeaveTypes();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await leaveService.getRequests(params);
      setRequests(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const response = await leaveService.getTypes();
      setLeaveTypes(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  };

  const openModal = () => {
    setFormData({
      leave_type_id: '',
      start_date: '',
      end_date: '',
      reason: ''
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      leave_type_id: '',
      start_date: '',
      end_date: '',
      reason: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await leaveService.submitRequest(formData);
      closeModal();
      fetchRequests();
    } catch (error) {
      console.error('Error submitting leave request:', error);
      alert(error.response?.data?.detail || 'Failed to submit leave request');
    }
  };

  const handleApprove = async (id) => {
    if (window.confirm('Are you sure you want to approve this leave request?')) {
      try {
        await leaveService.approveRequest(id, { approved_by: user?.id });
        fetchRequests();
      } catch (error) {
        console.error('Error approving leave request:', error);
        alert('Failed to approve leave request');
      }
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Enter rejection reason:');
    if (reason) {
      try {
        await leaveService.rejectRequest(id, {
          rejected_by: user?.id,
          rejection_reason: reason
        });
        fetchRequests();
      } catch (error) {
        console.error('Error rejecting leave request:', error);
        alert('Failed to reject leave request');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const canApprove = user?.role === 'admin' || user?.role === 'hr_manager';

  const columns = [
    {
      key: 'employee',
      label: 'Employee',
      render: (req) => req.employee ? `${req.employee.first_name} ${req.employee.last_name}` : '-'
    },
    {
      key: 'leave_type',
      label: 'Leave Type',
      render: (req) => req.leave_type?.name || '-'
    },
    {
      key: 'start_date',
      label: 'Start Date',
      render: (req) => formatDate(req.start_date)
    },
    {
      key: 'end_date',
      label: 'End Date',
      render: (req) => formatDate(req.end_date)
    },
    {
      key: 'days',
      label: 'Days',
      render: (req) => req.total_days || 0
    },
    {
      key: 'status',
      label: 'Status',
      render: (req) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(req.status)}`}>
          {req.status.toUpperCase()}
        </span>
      ),
    },
    {
      key: 'requested_on',
      label: 'Requested',
      render: (req) => formatDate(req.created_at)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (req) => (
        <div className="flex space-x-2">
          {canApprove && req.status === 'pending' && (
            <>
              <button
                onClick={() => handleApprove(req.id)}
                className="text-green-600 hover:text-green-900"
                title="Approve"
              >
                <CheckIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleReject(req.id)}
                className="text-red-600 hover:text-red-900"
                title="Reject"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Leave Requests</h1>
        <Button onClick={openModal}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Submit Request
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Pending</h3>
          <p className="mt-2 text-3xl font-semibold text-yellow-600">
            {requests.filter(r => r.status === 'pending').length}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Approved</h3>
          <p className="mt-2 text-3xl font-semibold text-green-600">
            {requests.filter(r => r.status === 'approved').length}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Rejected</h3>
          <p className="mt-2 text-3xl font-semibold text-red-600">
            {requests.filter(r => r.status === 'rejected').length}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Total</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900">
            {requests.length}
          </p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-4 border-b border-gray-200">
          <div className="flex space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={requests}
          loading={loading}
        />
      </div>

      {/* Submit Leave Request Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title="Submit Leave Request"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Leave Type *
            </label>
            <select
              required
              value={formData.leave_type_id}
              onChange={(e) => setFormData({ ...formData, leave_type_id: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select Leave Type</option>
              {leaveTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name} ({type.days_allowed} days available)
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Start Date *
              </label>
              <input
                type="date"
                required
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                End Date *
              </label>
              <input
                type="date"
                required
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Reason *
            </label>
            <textarea
              required
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Please provide a reason for your leave request..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit">Submit Request</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
